import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Upload, ImageIcon } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Text } from '@/components/ui/Text'

interface DualLogoPickerProps {
  logoUrl: string | null
  logoUrlOnDark: string | null
  onLogoFileChange: (file: File) => void
  onLogoDarkFileChange: (file: File) => void
  vertical?: boolean
}

function LogoDropZone({
  label,
  logoUrl,
  onFileChange,
  bg,
  testIdSuffix,
}: {
  label: string
  logoUrl: string | null
  onFileChange: (file: File) => void
  bg: 'light' | 'dark'
  testIdSuffix: string
}) {
  const inputRef = useRef<HTMLInputElement>(null)

  function handleClick() {
    inputRef.current?.click()
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) onFileChange(file)
    e.target.value = ''
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    const file = e.dataTransfer.files?.[0]
    if (file?.type.startsWith('image/')) onFileChange(file)
  }

  // The `bg` prop is fixed per tile (one light preview, one dark preview) and is
  // NOT tied to the user's current theme. The tile colors are inherent to the
  // preview-tile design: a white surface to preview the on-light logo and a
  // near-black surface to preview the on-dark logo, regardless of the app theme
  // or any white-label preset. They are intentionally hardcoded constants so the
  // preview stays neutral when presets override `--background`/`--card`.
  const isDarkPreview = bg === 'dark'

  return (
    <button
      type="button"
      data-testid={`dual-logo-${testIdSuffix}`}
      onClick={handleClick}
      onDrop={handleDrop}
      onDragOver={(e) => e.preventDefault()}
      className={cn(
        'flex flex-1 flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-4 transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isDarkPreview
          ? 'border-neutral-600 bg-neutral-900 hover:border-neutral-500'
          : 'border-border bg-white hover:border-muted-foreground/40',
      )}
    >
      {logoUrl ? (
        <img
          src={logoUrl}
          alt={label}
          className="max-h-12 max-w-[120px] object-contain"
        />
      ) : (
        <div className="flex flex-col items-center gap-1">
          {isDarkPreview ? (
            <ImageIcon className="h-6 w-6 text-neutral-500" />
          ) : (
            <Upload className="h-6 w-6 text-muted-foreground" />
          )}
        </div>
      )}
      <Text
        size="xs"
        className={cn(isDarkPreview ? 'text-neutral-400' : 'text-muted-foreground')}
      >
        {label}
      </Text>

      <input
        ref={inputRef}
        type="file"
        accept=".svg,.png,.jpg,.jpeg,image/svg+xml,image/png,image/jpeg"
        onChange={handleChange}
        className="hidden"
      />
    </button>
  )
}

export function DualLogoPicker({
  logoUrl,
  logoUrlOnDark,
  onLogoFileChange,
  onLogoDarkFileChange,
  vertical,
}: DualLogoPickerProps) {
  const { t } = useTranslation('app')
  return (
    <div className={cn('flex flex-1 gap-2', vertical ? 'flex-col' : '')}>
      <LogoDropZone
        label={t('brand.light')}
        logoUrl={logoUrl}
        onFileChange={onLogoFileChange}
        bg="light"
        testIdSuffix="light"
      />
      <LogoDropZone
        label={t('brand.dark')}
        logoUrl={logoUrlOnDark}
        onFileChange={onLogoDarkFileChange}
        bg="dark"
        testIdSuffix="dark"
      />
    </div>
  )
}
